import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { X, Heart, MoonStar } from "lucide-react";
import { useT, Rich } from "@/components/contentContext";

const API = process.env.REACT_APP_BACKEND_URL;

const AMOUNTS = [5, 10, 25];

const fmt = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

const DonateModal = ({ onClose }) => {
  const t = useT();
  const [offerings, setOfferings] = useState(null);
  const [name, setName] = useState("");
  const [amount, setAmount] = useState(10);
  const [custom, setCustom] = useState("");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetch(`${API}/api/donations/approved`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error())))
      .then((d) => setOfferings(d || []))
      .catch(() => setOfferings([]));
  }, []);

  const chosen = Number.isFinite(parseFloat(custom)) && parseFloat(custom) > 0 ? parseFloat(custom) : amount;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!(chosen >= 1)) {
      setError("Choose an amount of at least $1.");
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/donate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, comment, amount: chosen, origin_url: window.location.origin }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Could not begin the offering");
      const data = await res.json();
      window.location.href = data.checkout_url;
    } catch (err) {
      setError(err.message || "Could not begin the offering.");
      setBusy(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] flex items-center justify-center bg-ink/92 p-4 backdrop-blur-xl"
      onClick={onClose}
      data-testid="donate-modal"
    >
      <motion.div
        initial={{ opacity: 0, y: 32, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 24, scale: 0.97 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto border border-gold/30 bg-surface p-6 md:p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="absolute right-4 top-4 cursor-pointer text-slate-500 transition-colors hover:text-silver"
          onClick={onClose}
          data-testid="donate-close"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>

        <p className="flex items-center gap-3 font-jmono text-[10px] uppercase tracking-[0.35em] text-gold">
          <Heart className="h-3.5 w-3.5" />
          {t("modal.support.heading")}
        </p>
        <h3 className="mt-4 font-display text-2xl text-silver">Leave an offering</h3>
        <Rich as="p" text={t("modal.support.body")} className="mt-3 text-sm font-light leading-relaxed text-slate-400" />

        <div className="mt-7">
          <p className="font-jmono text-[10px] uppercase tracking-[0.3em] text-slate-500">Recent offerings</p>
          <div className="mt-3 max-h-44 space-y-3 overflow-y-auto pr-2" data-testid="offerings-list">
            {offerings === null && (
              <p className="text-xs text-slate-600">Gathering the wall…</p>
            )}
            {offerings && offerings.length === 0 && (
              <p className="font-cormorant text-base italic text-slate-600">
                Be the first to leave an offering.
              </p>
            )}
            {(offerings || []).map((o, i) => (
              <div key={o.id} className="border-l-2 border-gold/40 pl-4" data-testid={`offering-item-${i}`}>
                <p className="text-sm text-silver">
                  {o.name} <span className="font-jmono text-[10px] tracking-[0.2em] text-gold">· {fmt(o.amount)}</span>
                </p>
                {o.comment && (
                  <p className="mt-1 text-sm font-light italic leading-relaxed text-slate-400">"{o.comment}"</p>
                )}
              </div>
            ))}
          </div>
        </div>

        <form onSubmit={submit} className="mt-7 space-y-5">
          <div>
            <label className="mb-2.5 block font-jmono text-[10px] uppercase tracking-[0.3em] text-slate-500">
              Your name (or stay shadowed)
            </label>
            <input
              className="input-night"
              placeholder="Anonymous"
              value={name}
              maxLength={80}
              onChange={(e) => setName(e.target.value)}
              data-testid="donate-name"
            />
          </div>

          <div>
            <label className="mb-2.5 block font-jmono text-[10px] uppercase tracking-[0.3em] text-slate-500">
              The offering
            </label>
            <div className="flex flex-wrap gap-2">
              {AMOUNTS.map((a) => (
                <button
                  key={a}
                  type="button"
                  className={`cursor-pointer border px-4 py-2 font-jmono text-xs tracking-[0.2em] transition-colors duration-300 ${
                    amount === a && !custom ? "border-gold/70 text-gold" : "border-line text-slate-400 hover:text-silver"
                  }`}
                  onClick={() => {
                    setAmount(a);
                    setCustom("");
                  }}
                  data-testid={`donate-amount-chip-${a}`}
                >
                  ${a}
                </button>
              ))}
              <div className="relative flex-1 min-w-[120px]">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 font-jmono text-xs text-slate-500">
                  $
                </span>
                <input
                  className="input-night !py-2 pl-7 text-sm"
                  type="number"
                  min="1"
                  max="10000"
                  step="1"
                  placeholder="Custom"
                  value={custom}
                  onChange={(e) => setCustom(e.target.value)}
                  data-testid="donate-amount-custom"
                />
              </div>
            </div>
          </div>

          <div>
            <label className="mb-2.5 block font-jmono text-[10px] uppercase tracking-[0.3em] text-slate-500">
              A few words for the wall (optional)
            </label>
            <textarea
              className="input-night min-h-[88px] resize-y"
              placeholder="For the music…"
              value={comment}
              maxLength={500}
              onChange={(e) => setComment(e.target.value)}
              data-testid="donate-comment"
            />
          </div>

          {error && (
            <p className="text-xs text-red-500" data-testid="donate-error">
              {error}
            </p>
          )}

          <button className="btn-night w-full" type="submit" disabled={busy} data-testid="donate-submit">
            {busy ? "One gentle moment…" : `Offer ${fmt(chosen || 0)}`}
          </button>
          <p className="flex items-center justify-center gap-2 text-center text-[10px] text-slate-600">
            <MoonStar className="h-3 w-3" />
            Charged in USD via Stripe · words appear once approved
          </p>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default DonateModal;
